import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Shared/contexts/AuthContext';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import { Navbar } from '../../Shared/components/Navbar';
import { LoadingSpinner } from '../../Shared/components/LoadingSpinner';
import { Button } from '../../Shared/ui/button';
import { Card } from '../../Shared/ui/card';
import type { ActiveWorksheetResponse } from '../../Shared/types';

const StudentHome: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeWorksheet, setActiveWorksheet] = useState<ActiveWorksheetResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadActiveWorksheet = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.getActiveWorksheet();
      setActiveWorksheet(response);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosErr.response?.status === 404) {
        setActiveWorksheet(null); // no worksheet yet — show start prompt
      } else {
        setError(extractApiError(axiosErr));
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadActiveWorksheet();
  }, [loadActiveWorksheet]);

  const handleStartWorksheet = (): void => {
    navigate('/worksheet');
  };

  const getWorksheetButtonText = (): string => {
    if (!activeWorksheet) {
      return 'Start Worksheet';
    }

    const { data } = activeWorksheet;
    if (data.status === 'in_progress') {
      return 'Continue Worksheet';
    }
    return 'Start Worksheet';
  };

  const getWorksheetDescription = (): string => {
    if (!activeWorksheet) {
      return '';
    }

    const { type, topic, data } = activeWorksheet;

    if (type === 'diagnostic') {
      return 'Complete your diagnostic assessment to determine your starting point';
    }

    if (topic && data.worksheet) {
      const packNum = data.pack_number ?? 0;
      const seqIdx = data.sequence_index ?? 0;
      return `${topic.name} - Pack ${packNum}, Worksheet ${seqIdx}`;
    }

    return 'Continue your learning journey';
  };

  const isDiagnosticCompleted = user?.diagnostic_completed_at !== null;

  return (
    <Navbar>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'Student'}! 👋
          </h1>
          <p className="text-lg text-gray-600">
            {isDiagnosticCompleted
              ? "Let's continue your learning journey"
              : "Let's start with a quick diagnostic assessment"}
          </p>
        </div>

        {/* Active Worksheet Card */}
        <Card className="mb-6">
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => void loadActiveWorksheet()} variant="secondary">
                Try Again
              </Button>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {activeWorksheet?.type === 'diagnostic'
                    ? '🎯 Diagnostic Assessment'
                    : '📚 Current Worksheet'}
                </h2>
                <p className="text-gray-600">{getWorksheetDescription()}</p>
              </div>

              {activeWorksheet?.data && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Progress</p>
                      <p className="text-lg font-semibold text-gray-900">
                        Question {(activeWorksheet.data.current_question_index ?? 0) + 1} of{' '}
                        {activeWorksheet.data.total_questions || 20}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 mb-1">Status</p>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {activeWorksheet.data.status === 'in_progress'
                          ? 'In Progress'
                          : 'Not Started'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <Button onClick={handleStartWorksheet} className="w-full text-lg py-3">
                {getWorksheetButtonText()} →
              </Button>
            </div>
          )}
        </Card>

        {/* Grade and Topic Info */}
        {isDiagnosticCompleted && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="text-center">
              <p className="text-sm text-gray-600 mb-1">Grade Level</p>
              <p className="text-2xl font-bold text-gray-900">
                Grade {user?.student?.current_grade || 5}
              </p>
            </Card>
            <Card className="text-center">
              <p className="text-sm text-gray-600 mb-1">Current Topic</p>
              <p className="text-lg font-semibold text-gray-900">
                {activeWorksheet?.topic?.name ?? 'Not assigned yet'}
              </p>
            </Card>
          </div>
        )}
      </div>
    </Navbar>
  );
};

export default StudentHome;
